// Public brand inquiry form. Replaces self-serve brand signup at /login —
// new brands now go through BD qualification before an account is created.
//
// Visual register: matches the landing's studio-night money-app theme so the
// click-through from "Request access" feels coherent. Turnstile-gated.

import { useCallback, useEffect, useState } from 'react';
import { apiBase } from '../../lib/api';
import { PublicNav } from '../../components/nav/PublicNav';
import { Turnstile } from '../../components/Turnstile';

const API_BASE = apiBase();

const BUDGET_OPTIONS: { value: string; label: string }[] = [
  { value: 'under_5k', label: 'Under $5,000' },
  { value: '5k_to_25k', label: '$5,000 – $25,000' },
  { value: '25k_to_100k', label: '$25,000 – $100,000' },
  { value: '100k_plus', label: '$100,000+' },
  { value: 'lets_discuss', label: "Let's discuss" },
];

type SendState = 'idle' | 'sending' | 'sent' | 'captcha_failed' | 'error';

export function Inquire() {
  const [company, setCompany] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [intent, setIntent] = useState('');
  const [budget, setBudget] = useState('');
  const [state, setState] = useState<SendState>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Which offering this inquiry is for (Hub -> /clipping vs /localization).
  const isLoc =
    typeof location !== 'undefined' &&
    new URLSearchParams(location.search).get('service') === 'localization';
  const service = isLoc ? 'localization' : 'clipping';

  useEffect(() => {
    document.body.classList.add('studio-night');
    return () => document.body.classList.remove('studio-night');
  }, []);

  const onVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const onExpire = useCallback(() => setTurnstileToken(null), []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setState('sending');
    setErrMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: company,
          contact_name: contactName,
          contact_email: contactEmail,
          website_or_social: website,
          what_they_want: intent,
          budget_range: budget,
          service,
          turnstileToken,
          utm_source: new URLSearchParams(location.search).get('utm_source') || undefined,
          utm_campaign: new URLSearchParams(location.search).get('utm_campaign') || undefined,
        }),
      });
      if (r.status === 403) {
        setState('captcha_failed');
        return;
      }
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        setErrMsg(prettyError(body.error));
        setState('error');
        return;
      }
      setState('sent');
    } catch {
      setState('error');
      setErrMsg('Something went wrong. Please try again.');
    }
  }

  if (state === 'sent') {
    return (
      <>
        <PublicNav theme="night" onDarkHero={false} />
        <main
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: '#0c0c0d',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 520,
              padding: 56,
              background: 'linear-gradient(150deg, #1a1a1d, #0e0e10)',
              borderRadius: 16,
              border: '1px solid #2a2a30',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#F5F2EE',
                margin: '0 auto 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                color: '#08080A',
                fontWeight: 800,
              }}
            >
              ✓
            </div>
            <h1
              style={{
                fontSize: 32,
                fontWeight: 800,
                letterSpacing: '-0.025em',
                color: '#fff',
                marginBottom: 12,
              }}
            >
              Got it. Talk soon.
            </h1>
            <p style={{ color: '#a0a0aa', fontSize: 16, lineHeight: 1.55 }}>
              We'll review your inquiry and reach out within one business day. If you need
              us sooner, email{' '}
              <a href="mailto:hello@imorbis.com" style={{ color: '#F5F2EE' }}>
                hello@imorbis.com
              </a>
              .
            </p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <PublicNav theme="night" onDarkHero={false} />
      <main
        style={{
          minHeight: '100vh',
          padding: '120px 24px 64px',
          background: '#0c0c0d',
        }}
      >
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{ marginBottom: 32 }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#F5F2EE',
              }}
            >
              Request access
            </span>
            <h1
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: 'clamp(34px, 4.4vw, 50px)',
                fontWeight: 400,
                letterSpacing: '-0.01em',
                color: '#F5F2EE',
                marginTop: 14,
                lineHeight: 1.06,
              }}
            >
              {isLoc ? 'Tell us about your library.' : 'Brief us on your campaign.'}
            </h1>
            <p style={{ color: 'rgba(245,242,238,0.7)', marginTop: 12, fontSize: 16, lineHeight: 1.55 }}>
              {isLoc
                ? "Tell us what you've already produced and where you want it to travel. If it's a fit, we'll map the markets and run the localization with you."
                : "Studio is invite-only for brands. Tell us what you're working on. If it's a fit, we'll set up your account and run your first campaign with you."}
            </p>
          </div>

          <form
            onSubmit={submit}
            style={{
              padding: 32,
              background: 'linear-gradient(150deg, #131316, #0e0e10)',
              border: '1px solid #2a2a30',
              borderRadius: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <Field
              label="Company"
              required
              value={company}
              onChange={setCompany}
              placeholder="Acme Records"
              autoComplete="organization"
            />
            <Field
              label="Your name"
              required
              value={contactName}
              onChange={setContactName}
              placeholder="Jamie Lee"
              autoComplete="name"
            />
            <Field
              label="Work email"
              required
              type="email"
              value={contactEmail}
              onChange={setContactEmail}
              placeholder="jamie@acme.com"
              autoComplete="email"
            />
            <Field
              label="Website or main social"
              required
              value={website}
              onChange={setWebsite}
              placeholder="acme.com or @acme"
              autoComplete="url"
            />
            <FieldTextarea
              label="What do you want to do?"
              required
              value={intent}
              onChange={setIntent}
              placeholder="One paragraph: artist, release, song, content, or campaign goal. What does success look like?"
              rows={4}
            />
            <FieldSelect
              label="Budget range"
              required
              value={budget}
              onChange={setBudget}
              options={BUDGET_OPTIONS}
            />

            <div style={{ marginTop: 4 }}>
              <Turnstile onVerify={onVerify} onExpire={onExpire} theme="dark" />
            </div>

            <button
              type="submit"
              disabled={state === 'sending'}
              style={{
                marginTop: 6,
                padding: '15px 26px',
                background: '#F5F2EE',
                color: '#08080A',
                border: 'none',
                borderRadius: 11,
                fontWeight: 700,
                fontSize: 15,
                cursor: state === 'sending' ? 'wait' : 'pointer',
                opacity: state === 'sending' ? 0.7 : 1,
                transition: 'transform 160ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 160ms',
              }}
            >
              {state === 'sending' ? 'Sending…' : 'Send inquiry'}
            </button>

            {state === 'captcha_failed' && (
              <p style={{ color: '#e88a8a', fontSize: 13, margin: 0 }}>
                Please complete the verification and try again.
              </p>
            )}
            {state === 'error' && (
              <p style={{ color: '#e88a8a', fontSize: 13, margin: 0 }}>
                {errMsg ?? 'Something went wrong. Please try again.'}
              </p>
            )}
          </form>

          <p
            style={{
              marginTop: 24,
              fontSize: 13,
              color: '#7a7a82',
              textAlign: 'center',
            }}
          >
            Already a client?{' '}
            <a href="/login" style={{ color: '#F5F2EE' }}>
              Sign in
            </a>
          </p>
        </div>
      </main>
    </>
  );
}

function prettyError(code: string | undefined): string {
  switch (code) {
    case 'company_name_required':
      return 'Company name is required.';
    case 'contact_name_required':
      return 'Your name is required.';
    case 'invalid_email':
      return "That email doesn't look right.";
    case 'website_or_social_required':
      return 'A website or social handle is required.';
    case 'what_they_want_required':
      return 'Tell us a bit about what you want to do.';
    case 'invalid_budget_range':
      return 'Pick a budget range.';
    case 'captcha_failed':
      return 'Verification failed. Please try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: '#0c0c0d',
  color: '#fff',
  border: '1px solid #2a2a30',
  borderRadius: 9,
  fontSize: 15,
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 160ms',
};

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label
      style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 600,
        color: '#d0d0d6',
        marginBottom: 6,
        letterSpacing: '-0.005em',
      }}
    >
      {children}
      {required && <span style={{ color: '#F5F2EE', marginLeft: 4 }}>*</span>}
    </label>
  );
}

function Field({
  label,
  required,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={inputBaseStyle}
      />
    </div>
  );
}

function FieldTextarea({
  label,
  required,
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      <textarea
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{ ...inputBaseStyle, resize: 'vertical', lineHeight: 1.5 }}
      />
    </div>
  );
}

function FieldSelect({
  label,
  required,
  value,
  onChange,
  options,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      <select
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputBaseStyle, appearance: 'none', cursor: 'pointer' }}
      >
        <option value="" disabled>
          Choose a range…
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
